import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import webinarHeroBg from "@/assets/webinar-hero-bg.jpg";
import eifuCardImage from "@/assets/eifu-in-practice-card.webp";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Loader2, Filter, X, CalendarCheck, PlayCircle, Video, ArrowRight } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import KnowledgeHeroAnimation from "@/components/KnowledgeHeroAnimation";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import ResourceCard from "@/components/resources/ResourceCard";
import BlogSearch from "@/components/blog/BlogSearch";
import BlogCardSkeleton from "@/components/blog/BlogCardSkeleton";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { EXCLUDED_TAGS, type UnifiedResource } from "@/lib/resourceTypes";
import { useLanguage } from "@/hooks/useLanguage";
import { localizeContentRow } from "@/lib/localizeContent";
import { getKCT } from "@/i18n/knowledgeCenter";
import { localizeHref } from "@/lib/localizeHref";
import { expandFilterAliases } from "@/lib/filterAliases";

type StatusFilter = "all" | "upcoming" | "on-demand";

const Webinars = () => {
  const lang = useLanguage();
  const t = getKCT("webinars", lang);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("upcoming");
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [visibleCount, setVisibleCount] = useState(18);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const { data: rawWebinars, isLoading } = useQuery({
    queryKey: ["webinars-listing", lang],
    queryFn: async () => {
      // Fetch parent webinars
      const { data, error } = await supabase
        .from("webinars")
        .select("id, slug, slug_es, slug_zh, title, title_es, title_zh, summary, summary_es, summary_zh, content, content_es, content_zh, featured_image, category, service_category, published_at, author, tags, webinar_date, recording_url, speakers, duration_minutes, parent_slug, lang" as any)
        .is("parent_slug", null)
        .not("published_at", "is", null)
        .order("webinar_date", { ascending: false, nullsFirst: false })
        .order("published_at", { ascending: false, nullsFirst: false });
      if (error) throw error;

      // Fetch child on-demand pages to know which parents have recordings
      const { data: children } = await supabase
        .from("webinars")
        .select("parent_slug, recording_url" as any)
        .not("parent_slug", "is", null)
        .not("recording_url", "is", null);
      const childRecordingSet = new Set(
        (children ?? []).map((c: any) => c.parent_slug as string)
      );

      return (data ?? [])
        .filter((row: any) => {
          const rowLang = row.lang || 'en';
          const tags = row.tags ?? [];
          const category = row.category ?? "";
          const isHidden = EXCLUDED_TAGS.some(
            (ex) => category.toLowerCase() === ex.toLowerCase() || tags.some((tag: string) => tag.toLowerCase() === ex.toLowerCase())
          );
          if (isHidden) return false;
          if (lang === 'en') return rowLang === 'en';
          if (lang === 'es') return rowLang === 'es' || row.slug_es || row.title_es;
          if (lang === 'zh-cn') return rowLang === 'zh-cn' || row.slug_zh || row.title_zh;
          return rowLang === 'en';
        })
        .map((row: any) => {
          const localized = localizeContentRow(row, lang);
          // Mark parent as having a recording if either itself or its child has one
          const hasRecording = !!row.recording_url || childRecordingSet.has(row.slug);
          return {
            ...localized,
            recording_url: hasRecording ? (row.recording_url || 'child') : null,
            tags: (row.tags ?? []).filter((t: string) => serviceLabelsSet.has(t)),
            contentType: "webinars" as const,
          } as UnifiedResource;
        });
    },
  });

  const webinars = rawWebinars ?? [];

  const filtered = useMemo(() => {
    let list = webinars;

    // Status filter
    if (statusFilter === "upcoming") {
      list = list.filter((w) => w.webinar_date && new Date(w.webinar_date).getTime() > Date.now());
    } else if (statusFilter === "on-demand") {
      list = list.filter(
        (w) =>
          !!w.recording_url &&
          (!w.webinar_date || new Date(w.webinar_date).getTime() <= Date.now())
      );
    }

    // Tag/category filter
    if (selectedFilters.length > 0) {
      const expanded = expandFilterAliases(selectedFilters);
      list = list.filter((r) =>
        expanded.some((f) => r.category === f || (r.tags ?? []).includes(f))
      );
    }

    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (r) => r.title.toLowerCase().includes(q) || (r.summary ?? "").toLowerCase().includes(q)
      );
    }

    return list;
  }, [webinars, statusFilter, selectedFilters, searchQuery]);

  const visibleWebinars = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  // Collect available tag filters (services only)
  const availableFilters = useMemo(() => {
    const set = new Set<string>();
    for (const r of webinars) {
      for (const t of r.tags ?? []) set.add(t);
      if (r.category) set.add(r.category);
    }
    return [...set].sort();
  }, [webinars]);

  // Counts
  const upcomingCount = useMemo(
    () => webinars.filter((w) => w.webinar_date && new Date(w.webinar_date).getTime() > Date.now()).length,
    [webinars]
  );
  const onDemandCount = useMemo(
    () =>
      webinars.filter(
        (w) => !!w.recording_url && (!w.webinar_date || new Date(w.webinar_date).getTime() <= Date.now())
      ).length,
    [webinars]
  );

  // Infinite scroll
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) setVisibleCount((c) => c + 18);
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore]);

  useEffect(() => {
    setVisibleCount(18);
  }, [statusFilter, selectedFilters, searchQuery]);

  const handleSearch = useCallback((q: string) => setSearchQuery(q), []);

  const statusTabs: { key: StatusFilter; label: string; icon: React.ReactNode; count: number }[] = [
    { key: "all", label: t.tabAll, icon: <Video size={14} />, count: webinars.length },
    { key: "upcoming", label: t.tabUpcoming, icon: <CalendarCheck size={14} />, count: upcomingCount },
    { key: "on-demand", label: t.tabOnDemand, icon: <PlayCircle size={14} />, count: onDemandCount },
  ];

  const langPrefix = lang === "en" ? "/en" : lang === "es" ? "/es" : "/zh-cn";
  const canonicalPath = lang === "es" ? "/es/webinars" : lang === "zh-cn" ? "/zh-cn/webinars" : "/en/webinars";

  // JSON-LD structured data
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: t.title,
    description: t.description,
    url: `https://qbdgroup.com${canonicalPath}`,
    publisher: {
      "@type": "Organization",
      name: "QbD Group",
      url: "https://qbdgroup.com",
    },
  };

  return (
    <div className="min-h-screen">
      <ScrollToTop />
      <Navbar />
      <SEOHead
        title={t.title}
        description={t.description}
        canonical={`https://qbdgroup.com${canonicalPath}`}
        ogImage={webinarHeroBg}
      >
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </SEOHead>
      <BreadcrumbJsonLd items={[{ name: t.breadcrumbKC, path: localizeHref("/en/knowledge-center", lang) }, { name: t.breadcrumbPage }]} />

      {/* Hero */}
      <section className="relative pt-36 pb-20 sm:pb-28 bg-primary overflow-hidden">
        <KnowledgeHeroAnimation />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-accent/5 -translate-y-1/2 translate-x-1/3 blur-3xl pointer-events-none" />
        <div className="relative z-10 container mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <nav aria-label="Breadcrumb" className="mb-8 mt-4">
              <ol className="flex items-center gap-2 text-xs text-primary-foreground/40 tracking-wide uppercase">
                <li>
                  <a href={localizeHref("/", lang)} className="hover:text-primary-foreground/70 transition-colors">{lang === "zh-cn" ? "首页" : lang === "es" ? "Inicio" : "Home"}</a>
                </li>
                <li aria-hidden="true" className="text-primary-foreground/20">/</li>
                <li>
                  <a href={localizeHref("/en/knowledge-center", lang)} className="hover:text-primary-foreground/70 transition-colors">{t.breadcrumbKC}</a>
                </li>
                <li aria-hidden="true" className="text-primary-foreground/20">/</li>
                <li className="text-primary-foreground/60">{t.breadcrumbPage}</li>
              </ol>
            </nav>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground tracking-tight leading-[1.1]">
              {t.heroTitle}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-primary-foreground/60 max-w-xl leading-relaxed">
              {t.heroDesc}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-qbd-ice">
        <div className="container mx-auto px-6 lg:px-10">
          {/* Status tabs */}
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            {statusTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full transition-all ${
                  statusFilter === tab.key
                    ? "bg-primary text-primary-foreground shadow-md"
                    : "bg-background text-foreground/70 hover:bg-primary/5 border border-border"
                }`}
              >
                {tab.icon}
                {tab.label}
                <span
                  className={`ml-1 text-xs rounded-full w-5 h-5 flex items-center justify-center ${
                    statusFilter === tab.key
                      ? "bg-primary-foreground/20 text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Top bar */}
          <div className="mb-6 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                className="lg:hidden gap-2"
                onClick={() => setShowMobileFilters(!showMobileFilters)}
              >
                <Filter size={14} />
                {t.filters}
                {selectedFilters.length > 0 && (
                  <span className="ml-1 text-xs bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center">
                    {selectedFilters.length}
                  </span>
                )}
              </Button>
              {selectedFilters.map((f) => (
                <button
                  key={f}
                  onClick={() => setSelectedFilters(selectedFilters.filter((x) => x !== f))}
                  className="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium bg-primary/8 text-primary rounded-full hover:bg-primary/15 transition-colors"
                >
                  {trFilterLabel(f, lang)} <X size={12} />
                </button>
              ))}
              {selectedFilters.length > 1 && (
                <button
                  onClick={() => setSelectedFilters([])}
                  className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t.clearAllShort}
                </button>
              )}
            </div>
            <span className="text-sm text-muted-foreground shrink-0">
              {filtered.length > 0 && `${filtered.length} ${t.webinarCount}`}
            </span>
          </div>

          {/* Mobile filters */}
          {showMobileFilters && (
            <div className="lg:hidden mb-6 bg-background border border-border rounded-xl p-4">
              <WebinarSidebar
                selectedFilters={selectedFilters}
                onFilterChange={(f) => { setSelectedFilters(f); setShowMobileFilters(false); }}
                availableFilters={availableFilters}
                lang={lang}
              />
            </div>
          )}

          <div className="flex gap-10">
            {/* Desktop sidebar */}
            <div className="hidden lg:block w-64 shrink-0">
              <div className="sticky top-36 space-y-6">
                <BlogSearch onSearch={handleSearch} placeholder={lang === "zh-cn" ? "搜索网络研讨会..." : lang === "es" ? "Buscar webinars..." : "Search webinars..."} />
                <WebinarSidebar
                  selectedFilters={selectedFilters}
                  onFilterChange={setSelectedFilters}
                  availableFilters={availableFilters}
                  lang={lang}
                />
              </div>
            </div>

            {/* Main content */}
            <main className="flex-1 min-w-0">
              {isLoading ? (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <BlogCardSkeleton key={i} />
                  ))}
                </div>
              ) : visibleWebinars.length === 0 ? (
                <div className="text-center py-20">
                  <p className="text-muted-foreground text-lg mb-2">
                    {searchQuery
                      ? `${t.noResults} "${searchQuery}".`
                      : selectedFilters.length > 0 || statusFilter !== "all"
                      ? t.noFilters
                      : t.noWebinars}
                  </p>
                  {(searchQuery || selectedFilters.length > 0 || statusFilter !== "all") && (
                    <button
                      onClick={() => { setSearchQuery(""); setSelectedFilters([]); setStatusFilter("all"); }}
                      className="text-sm text-qbd-blue font-semibold hover:underline"
                    >
                      {t.clearAll}
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                    {statusFilter !== "upcoming" && <EifuPromoCard lang={lang} />}
                    {visibleWebinars.map((r, i) => (
                      <ResourceCard key={r.id} resource={r} index={i + 1} />
                    ))}
                  </div>
                  <div ref={loadMoreRef} className="flex justify-center py-10">
                    {hasMore && <Loader2 className="animate-spin text-muted-foreground" size={24} />}
                    {!hasMore && filtered.length > 18 && (
                      <p className="text-sm text-muted-foreground">{t.reachedEnd}</p>
                    )}
                  </div>
                </>
              )}
            </main>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

/* ── Inline sidebar for service/topic filters ── */
import { serviceLinks, industryLinks } from "@/components/navbar/navData";
import { Monitor } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { SiteLang } from "@/hooks/useLanguage";
import { getKCT as getKCTStatic } from "@/i18n/knowledgeCenter";

const industryIconMap = Object.fromEntries(industryLinks.map((l) => [l.label, l.icon])) as Record<string, LucideIcon>;
industryIconMap["MDSW"] = Monitor;

const serviceLabels = serviceLinks.map((l) => l.label);
const serviceLabelsSet = new Set(serviceLabels);
const serviceColorMap = Object.fromEntries(serviceLinks.map((l) => [l.label, l.color]));

/** Translation map for filter labels (industries + services) */
const FILTER_LABEL_I18N: Record<string, { es?: string; "zh-cn"?: string }> = {
  // Industries
  "Pharma & Biotech": { es: "Pharma & Biotech", "zh-cn": "制药与生物技术" },
  "Medical Devices": { es: "Medical Devices", "zh-cn": "医疗器械" },
  "In Vitro Diagnostics": { es: "In Vitro Diagnostics", "zh-cn": "体外诊断" },
  "MDSW": { es: "MDSW", "zh-cn": "医疗器械软件" },
  // Services
  "Qualification & Validation": { es: "Qualification & Validation", "zh-cn": "确认与验证" },
  "Regulatory Affairs": { es: "Regulatory Affairs", "zh-cn": "法规事务" },
  "Clinical Solutions": { es: "Clinical Solutions", "zh-cn": "临床解决方案" },
  "Software Solutions & Services": { es: "Software Solutions & Services", "zh-cn": "软件解决方案与服务" },
  "Quality Assurance": { es: "Quality Assurance", "zh-cn": "质量保证" },
  "Audits": { es: "Auditorías", "zh-cn": "审计" },
  "Toxicology": { es: "Toxicología Regulatoria", "zh-cn": "法规毒理学" },
  "Regulatory Toxicology": { es: "Toxicología Regulatoria", "zh-cn": "法规毒理学" },
  "Lab Services": { es: "Lab Services", "zh-cn": "实验室服务" },
  "Pharmacovigilance": { es: "Pharmacovigilance", "zh-cn": "药物警戒" },
};

const trFilterLabel = (label: string, lang: SiteLang): string => {
  if (label === "Toxicology" && lang === "en") return "Regulatory Toxicology";
  const entry = FILTER_LABEL_I18N[label];
  if (!entry) return label;
  if (lang === "zh-cn") return entry["zh-cn"] ?? label;
  if (lang === "es") return entry.es ?? label;
  return label;
};

interface SidebarProps {
  selectedFilters: string[];
  onFilterChange: (f: string[]) => void;
  availableFilters: string[];
  lang: SiteLang;
}

const WebinarSidebar = ({ selectedFilters, onFilterChange, availableFilters, lang }: SidebarProps) => {
  const t = getKCTStatic("webinars", lang);
  const matchesCategory = (filter: string, category: string) =>
    filter === category || filter.includes(category) || category.includes(filter);

  const industries = [...industryLinks.map((l) => l.label), "MDSW"];
  const availableIndustries = industries.filter((i) =>
    availableFilters.some((f) => matchesCategory(f, i))
  );
  const availableServices = serviceLabels.filter((s) =>
    availableFilters.some((f) => matchesCategory(f, s))
  );
  const getFilterValue = (categoryLabel: string) =>
    availableFilters.find((f) => matchesCategory(f, categoryLabel)) ?? categoryLabel;

  const toggle = (val: string) => {
    onFilterChange(
      selectedFilters.includes(val) ? selectedFilters.filter((x) => x !== val) : [...selectedFilters, val]
    );
  };

  return (
    <aside className="space-y-6">
      {availableIndustries.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-foreground mb-2 px-3">{t.allIndustries}</h3>
          <div className="space-y-0.5">
            {availableIndustries.map((item) => {
              const filterVal = getFilterValue(item);
              const isActive = selectedFilters.includes(filterVal);
              const Icon = industryIconMap[item];
              return (
                <button
                  key={item}
                  onClick={() => toggle(filterVal)}
                  className={`block w-full text-left px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    isActive
                      ? "text-primary font-semibold bg-primary/5"
                      : "text-foreground/70 hover:text-primary hover:bg-primary/3"
                  }`}
                >
                  {Icon && <Icon size={14} className="inline-block mr-2 -mt-0.5" />}
                  {trFilterLabel(item, lang)}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {availableServices.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-foreground mb-2 px-3">{t.allServices}</h3>
          <div className="space-y-0.5">
            {availableServices.map((item) => {
              const filterVal = getFilterValue(item);
              const isActive = selectedFilters.includes(filterVal);
              const color = serviceColorMap[item];
              return (
                <button
                  key={item}
                  onClick={() => toggle(filterVal)}
                  className={`block w-full text-left px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    isActive
                      ? "text-primary font-semibold bg-primary/5"
                      : "text-foreground/70 hover:text-primary hover:bg-primary/3"
                  }`}
                >
                  {color && <span className="inline-block w-2 h-2 rounded-full mr-2 -mt-0.5 align-middle" style={{ backgroundColor: color }} />}
                  {trFilterLabel(item, lang)}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </aside>
  );
};

/* ── Curated promo card: eIFU in practice webinar (hosted on ifucare.io) ── */
const EIFU_PROMO_I18N: Record<string, { badge: string; title: string; summary: string; meta: string; cta: string }> = {
  en: {
    badge: "On-Demand Webinar",
    title: "eIFU in practice: from regulatory requirements to implementation",
    summary: "What compliant electronic Instructions for Use actually require, and how to close the gap between the regulation and a working, audit-ready process.",
    meta: "Recording available",
    cta: "Watch on IFUcare",
  },
  es: {
    badge: "Webinar bajo demanda",
    title: "eIFU en la práctica: de los requisitos regulatorios a la implementación",
    summary: "Qué exigen realmente las Instrucciones de Uso electrónicas conformes y cómo cerrar la brecha entre la normativa y un proceso operativo y auditable.",
    meta: "Grabación disponible",
    cta: "Ver en IFUcare",
  },
  "zh-cn": {
    badge: "点播研讨会",
    title: "eIFU 实践：从法规要求到落地实施",
    summary: "了解合规的电子使用说明书（eIFU）究竟有哪些要求，以及如何弥合法规与可审计的实际流程之间的差距。",
    meta: "录播已上线",
    cta: "在 IFUcare 观看",
  },
};

const EIFU_PROMO_URL = "https://ifucare.io/webinars/eifu-in-practice-from-regulatory-requirements-to-implementation";

const EifuPromoCard = ({ lang }: { lang: SiteLang }) => {
  const t = EIFU_PROMO_I18N[lang] ?? EIFU_PROMO_I18N.en;
  return (
    <a
      href={EIFU_PROMO_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col bg-background border rounded-2xl overflow-hidden hover:shadow-xl hover:shadow-qbd-blue/8 hover:border-qbd-blue/30 transition-all duration-300 h-full border-accent ring-1 ring-accent/30"
    >
      <div className="aspect-[16/10] overflow-hidden relative">
        <img
          src={eifuCardImage}
          alt={t.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
          width={1024}
          height={584}
        />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md text-white backdrop-blur-sm bg-primary/90">
          <CalendarCheck size={12} /> {t.badge}
        </span>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <h2 className="text-lg font-bold text-foreground leading-snug group-hover:text-primary transition-colors line-clamp-3 min-h-[3.75rem]">
          {t.title}
        </h2>
        <div className="flex items-center gap-2 mt-2.5 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md text-white bg-primary/85">
            IFUcare
          </span>
          <span className="text-xs text-muted-foreground/70 whitespace-nowrap">{t.meta}</span>
        </div>
        <p className="mt-2 text-sm text-muted-foreground line-clamp-5">{t.summary}</p>
        <span className="inline-flex items-center gap-1 mt-auto pt-4 text-sm font-semibold text-qbd-blue group-hover:gap-2 transition-all">
          {t.cta} <ArrowRight size={14} />
        </span>
      </div>
    </a>
  );
};

export default Webinars;
